import { Box, Button, Center, Container, Group, Stack, Text, UnstyledButton } from '@mantine/core';
import { useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { useListOrganisations } from '@/api/generated/organisations/organisations';
import {
  getListActionTokensQueryKey,
  useListActionTokens,
  useListUsers,
  useResendInvite
} from '@/api/generated/users/users';
import {
  CardSection,
  ListPagination,
  MobileListFilters,
  PageHeading,
  SearchField,
  SelectField,
  tableRowStyle,
  Toast
} from '@/components/common';
import { getInviteStatus, InviteStatusBadge } from '@/features/users/components/InviteStatusBadge';
import { formatDateTimeParts, formatRole } from '@/utils/format';
import { useLocalPagination } from '@/utils/useLocalPagination';

interface ActionToken {
  id: string;
  userId: string;
  userEmail: string;
  userFirstName: string;
  userLastName: string;
  purpose: string;
  createdAt: string;
  expiresAt: string;
  usedAt: string | null;
}

const invitationColumns: [string, number][] = [
  ['adminInvitations.colInvited', 220],
  ['platformAdmin.colOrganization', 200],
  ['adminOrgDetail.colRole', 130],
  ['adminInvitations.colSent', 100],
  ['adminInvitations.colExpires', 100],
  ['platformAdmin.colStatus', 130]
];

const initials = (firstName: string, lastName: string) =>
  `${(firstName ?? '').charAt(0)}${(lastName ?? '').charAt(0)}`.toUpperCase();

const cellStyle = { color: 'var(--color-text-secondary)' };

export const AdminInvitationsPage = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [orgFilter, setOrgFilter] = useState('ALL');
  const [statusFilter, setStatusFilter] = useState('ALL');

  // staleTime: 0 — invitee prihvaća pozivnicu iz posve druge sesije, bez
  // signala natrag ovom ekranu, isti razlog kao Invite Colleague.
  const { data, isLoading } = useListActionTokens(
    { purpose: 'INVITE' },
    { query: { staleTime: 0 } }
  );
  const invites = (data as ActionToken[] | undefined) ?? [];

  // action-tokens endpoint ne vraća organizaciju/rolu — cross-referenciramo
  // preko liste svih korisnika (koju app_admin ionako već smije vidjeti).
  // staleTime: 0 — bez ovoga, tek pozvani korisnik (novi red u action-tokens
  // listi, koja se OSVJEŽAVA odmah) ne bi bio nađen u zastarjeloj (do 5 min)
  // keširanoj listi korisnika, pa bi organizacija/uloga ispale prazne.
  const { data: usersData } = useListUsers(undefined, { query: { staleTime: 0 } });
  const usersById = useMemo(() => new Map((usersData ?? []).map((u) => [u.id, u])), [usersData]);

  const { data: orgsData } = useListOrganisations();
  const orgOptions = [
    { value: 'ALL', label: t('adminUsers.filterOrgPlaceholder') },
    ...(orgsData ?? []).map((org) => ({ value: org.id ?? '', label: org.name ?? '' }))
  ];
  const statusOptions = [
    { value: 'ALL', label: t('adminOrganizations.filterStatusAll') },
    { value: 'pending', label: t('users.invite.status.pending') },
    { value: 'approved', label: t('users.invite.status.approved') },
    { value: 'expired', label: t('users.invite.status.expired') }
  ];

  const enriched = useMemo(
    () =>
      invites.map((item) => {
        const user = usersById.get(item.userId);
        return {
          ...item,
          organisationId: user?.organisationId,
          orgName: user?.orgName,
          role: user?.role
        };
      }),
    [invites, usersById]
  );

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    return enriched.filter((item) => {
      if (
        query &&
        !`${item.userFirstName} ${item.userLastName} ${item.userEmail}`
          .toLowerCase()
          .includes(query)
      ) {
        return false;
      }
      if (orgFilter !== 'ALL' && item.organisationId !== orgFilter) return false;
      if (statusFilter !== 'ALL' && getInviteStatus(item.expiresAt, item.usedAt) !== statusFilter) {
        return false;
      }
      return true;
    });
  }, [enriched, search, orgFilter, statusFilter]);

  const sorted = useMemo(
    () =>
      [...filtered].sort(
        (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      ),
    [filtered]
  );

  const { page, setPage, pageSize, setPageSize, totalPages, pageItems, offset } =
    useLocalPagination(sorted);
  const resetPage = () => setPage(1);

  const invalidate = () =>
    queryClient.invalidateQueries({
      queryKey: getListActionTokensQueryKey({ purpose: 'INVITE' })
    });

  const [resendFeedback, setResendFeedback] = useState<{
    email: string;
    tone: 'success' | 'danger';
  } | null>(null);

  const resendMutation = useResendInvite({
    mutation: {
      meta: { skipGlobalErrorToast: true },
      onSuccess: (_data, variables) => {
        invalidate();
        const email = sorted.find((i) => i.userId === variables.id)?.userEmail ?? '';
        setResendFeedback({ email, tone: 'success' });
      },
      onError: (_error, variables) => {
        const email = sorted.find((i) => i.userId === variables.id)?.userEmail ?? '';
        setResendFeedback({ email, tone: 'danger' });
      }
    }
  });
  const handleResend = (userId: string) => {
    setResendFeedback(null);
    resendMutation.mutate({ id: userId });
  };

  const paginationLabel = t('users.invite.showing', {
    from: sorted.length === 0 ? 0 : offset + 1,
    to: offset + pageItems.length,
    total: sorted.length
  });

  return (
    <Container size="lg" px={0}>
      <Stack gap={24}>
        <Group justify="space-between" wrap="wrap" gap={12} visibleFrom="sm">
          <PageHeading
            title={t('adminInvitations.title')}
            subtitle={t('adminInvitations.subtitle')}
            style={{ flex: '1 1 auto' }}
          />
          <Button component={Link} to="/admin/invitations/new">
            {t('adminInvitations.inviteButton')}
          </Button>
        </Group>

        <PageHeading
          title={t('adminInvitations.title')}
          subtitle={t('adminInvitations.subtitle')}
          hiddenFrom="sm"
        />

        {resendFeedback && (
          <Toast tone={resendFeedback.tone}>
            {resendFeedback.tone === 'success'
              ? t('users.invite.resendSuccess', { email: resendFeedback.email })
              : t('users.invite.resendError', { email: resendFeedback.email })}
          </Toast>
        )}

        <Group gap={16} align="flex-end" wrap="wrap" visibleFrom="sm">
          <Box w={{ base: '100%', sm: 280 }}>
            <SearchField
              hideLabel
              placeholder={t('adminInvitations.searchPlaceholder')}
              value={search}
              onChange={(event) => {
                setSearch(event.currentTarget.value);
                resetPage();
              }}
            />
          </Box>
          <div style={{ width: 200 }}>
            <SelectField
              label={t('adminUsers.filterOrg')}
              data={orgOptions}
              value={orgFilter}
              onChange={(value) => {
                setOrgFilter(value ?? 'ALL');
                resetPage();
              }}
              allowDeselect={false}
            />
          </div>
          <div style={{ width: 160 }}>
            <SelectField
              label={t('adminOrganizations.filterStatus')}
              data={statusOptions}
              value={statusFilter}
              onChange={(value) => {
                setStatusFilter(value ?? 'ALL');
                resetPage();
              }}
              allowDeselect={false}
            />
          </div>
        </Group>

        <Box hiddenFrom="sm">
          <MobileListFilters
            search={
              <SearchField
                hideLabel
                placeholder={t('adminInvitations.searchPlaceholder')}
                value={search}
                onChange={(event) => {
                  setSearch(event.currentTarget.value);
                  resetPage();
                }}
              />
            }
            filters={[
              {
                key: 'org',
                label: t('adminUsers.filterOrg'),
                options: orgOptions,
                value: orgFilter,
                onChange: (value) => {
                  setOrgFilter(value ?? 'ALL');
                  resetPage();
                },
                emptyValue: 'ALL'
              },
              {
                key: 'status',
                label: t('adminOrganizations.filterStatus'),
                options: statusOptions,
                value: statusFilter,
                onChange: (value) => {
                  setStatusFilter(value ?? 'ALL');
                  resetPage();
                },
                emptyValue: 'ALL',
                variant: 'buttons'
              }
            ]}
          />
        </Box>

        <CardSection title={t('adminInvitations.tableTitle')}>
          {!isLoading && sorted.length === 0 && (
            <Center py={40}>
              <Text fz={13} style={{ color: 'var(--color-text-muted)' }}>
                {t('users.invite.emptyState')}
              </Text>
            </Center>
          )}

          {sorted.length > 0 && (
            <>
              <Box style={{ overflowX: 'auto' }} visibleFrom="sm">
                <div style={{ minWidth: 1000 }}>
                  <div
                    className="table-header-row"
                    style={{
                      ...tableRowStyle,
                      padding: '12px 20px',
                      backgroundColor: 'var(--color-bg-canvas)',
                      borderBottom: '1px solid var(--color-border-soft)'
                    }}
                  >
                    {invitationColumns.map(([key, width]) => (
                      <Text
                        key={key}
                        fz={10}
                        fw={600}
                        w={width}
                        style={{ color: 'var(--color-text-muted)', letterSpacing: 0.4 }}
                      >
                        {t(key)}
                      </Text>
                    ))}
                    <Text
                      fz={10}
                      fw={600}
                      w={100}
                      ta="right"
                      style={{ color: 'var(--color-text-muted)', letterSpacing: 0.4 }}
                    >
                      {t('platformAdmin.colAction')}
                    </Text>
                  </div>

                  {pageItems.map((item, index) => {
                    const status = getInviteStatus(item.expiresAt, item.usedAt);
                    const sent = formatDateTimeParts(item.createdAt);
                    const expires = formatDateTimeParts(item.expiresAt);

                    return (
                      <div
                        key={item.id}
                        style={{
                          ...tableRowStyle,
                          padding: '14px 20px',
                          borderTop: index === 0 ? undefined : '1px solid var(--color-border-soft)'
                        }}
                      >
                        <Group gap={10} wrap="nowrap" w={220}>
                          <Center
                            w={28}
                            h={28}
                            style={{
                              borderRadius: 999,
                              backgroundColor: 'var(--color-bg-surface-2)',
                              border: '1px solid var(--color-border-default)',
                              flexShrink: 0
                            }}
                          >
                            <Text fz={10} fw={600} style={{ color: 'var(--color-text-muted)' }}>
                              {initials(item.userFirstName, item.userLastName)}
                            </Text>
                          </Center>
                          <Stack gap={0}>
                            <Text fz={12.5} style={{ color: 'var(--color-text-primary)' }}>
                              {item.userFirstName} {item.userLastName}
                            </Text>
                            <Text fz={10} style={{ color: 'var(--color-text-muted)' }}>
                              {item.userEmail}
                            </Text>
                          </Stack>
                        </Group>
                        <Text
                          fz={13}
                          fw={500}
                          w={200}
                          style={{ color: 'var(--color-text-secondary)' }}
                        >
                          {item.orgName ?? '—'}
                        </Text>
                        <Text fz={13} w={130} style={{ color: 'var(--color-text-secondary)' }}>
                          {formatRole(item.role)}
                        </Text>
                        <Stack gap={0} w={100}>
                          <Text fz={10.5} style={cellStyle}>
                            {sent.date}
                          </Text>
                          <Text fz={9.5} style={{ color: 'var(--color-text-muted)' }}>
                            {sent.time}
                          </Text>
                        </Stack>
                        <Stack gap={0} w={100}>
                          <Text fz={10.5} style={cellStyle}>
                            {expires.date}
                          </Text>
                          <Text fz={9.5} style={{ color: 'var(--color-text-muted)' }}>
                            {expires.time}
                          </Text>
                        </Stack>
                        <div style={{ width: 130 }}>
                          <InviteStatusBadge status={status} />
                        </div>
                        <Group w={100} justify="flex-end">
                          {status === 'expired' && (
                            <UnstyledButton
                              onClick={() => handleResend(item.userId)}
                              disabled={
                                resendMutation.isPending &&
                                resendMutation.variables?.id === item.userId
                              }
                              style={{ color: 'var(--color-text-primary)' }}
                            >
                              <Text fz={12} fw={600} style={{ color: 'inherit' }}>
                                {t('adminInvitations.resendLink')}
                              </Text>
                            </UnstyledButton>
                          )}
                        </Group>
                      </div>
                    );
                  })}
                </div>
              </Box>

              <Stack hiddenFrom="sm" gap={10} p={16}>
                {pageItems.map((item) => {
                  const status = getInviteStatus(item.expiresAt, item.usedAt);
                  const sent = formatDateTimeParts(item.createdAt);
                  const expires = formatDateTimeParts(item.expiresAt);

                  return (
                    <Stack
                      key={item.id}
                      gap={12}
                      p={16}
                      style={{
                        borderRadius: 12,
                        border: '1px solid var(--color-border-default)',
                        backgroundColor: 'var(--color-bg-surface)'
                      }}
                    >
                      <Group justify="space-between" align="center" wrap="nowrap">
                        <Text fz={13.5} fw={600} style={{ color: 'var(--color-text-primary)' }}>
                          {item.userFirstName} {item.userLastName}
                        </Text>
                        <InviteStatusBadge status={status} />
                      </Group>
                      <Text fz={11.5} style={{ color: 'var(--color-text-secondary)' }}>
                        {item.userEmail}
                      </Text>
                      <Group gap={12} wrap="wrap">
                        <Group gap={4} wrap="nowrap">
                          <Text fz={11} fw={600} style={{ color: 'var(--color-text-muted)' }}>
                            {t('platformAdmin.colOrganization')}:
                          </Text>
                          <Text fz={11} style={{ color: 'var(--color-text-secondary)' }}>
                            {item.orgName ?? '—'}
                          </Text>
                        </Group>
                        <Group gap={4} wrap="nowrap">
                          <Text fz={11} fw={600} style={{ color: 'var(--color-text-muted)' }}>
                            {t('adminOrgDetail.colRole')}:
                          </Text>
                          <Text fz={11} style={{ color: 'var(--color-text-secondary)' }}>
                            {formatRole(item.role)}
                          </Text>
                        </Group>
                      </Group>
                      <Group gap={4} wrap="wrap">
                        <Text fz={11} fw={600} style={{ color: 'var(--color-text-muted)' }}>
                          {t('adminInvitations.colSent')}:
                        </Text>
                        <Text fz={11} style={{ color: 'var(--color-text-secondary)' }}>
                          {sent.date} {sent.time}
                        </Text>
                        <Text fz={11} fw={600} style={{ color: 'var(--color-text-muted)' }}>
                          | {t('adminInvitations.colExpires')}:
                        </Text>
                        <Text fz={11} style={{ color: 'var(--color-text-secondary)' }}>
                          {expires.date} {expires.time}
                        </Text>
                      </Group>
                      {status === 'expired' && (
                        <Box pt={8} style={{ borderTop: '1px solid var(--color-border-soft)' }}>
                          <Group justify="flex-end">
                            <UnstyledButton
                              onClick={() => handleResend(item.userId)}
                              disabled={
                                resendMutation.isPending &&
                                resendMutation.variables?.id === item.userId
                              }
                            >
                              <Text
                                fz={12}
                                fw={600}
                                style={{
                                  color: 'var(--color-accent-primary-active)',
                                  textDecoration: 'underline'
                                }}
                              >
                                {t('adminInvitations.resendLink')}
                              </Text>
                            </UnstyledButton>
                          </Group>
                        </Box>
                      )}
                    </Stack>
                  );
                })}
              </Stack>

              <ListPagination
                label={paginationLabel}
                page={page}
                totalPages={totalPages}
                onPageChange={setPage}
                pageSize={pageSize}
                onPageSizeChange={setPageSize}
              />
            </>
          )}
        </CardSection>
      </Stack>
    </Container>
  );
};
